import { useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "motion/react";
import { auctionItems } from "@/mocks/auctionItems";
import { auctionToNormalBidData, auctionToReverseBidData } from "@/utils/auctionToBidData";
import { calculateBid } from "@/pages/bid-calculator/components/BidResult";
import { calculateReverse } from "@/pages/bid-calculator/components/ReverseResult";

type AuctionItem = typeof auctionItems[number];

interface CompareEntry {
  item: AuctionItem;
  normalRec: number;
  normalSafe: number;
  normalROI: number;
  revMax: number;
  revSafe: number;
  revROI: number;
  revDanger: boolean;
  revWarning: boolean;
  totalCost: number;
  repairCost: number;
  acquisitionTax: number;
  score: number;
}

function fmtShort(n: number): string {
  const abs = Math.abs(n);
  const sign = n < 0 ? "-" : "";
  if (abs >= 100000000) return sign + (abs / 100000000).toFixed(1) + "억";
  if (abs >= 10000) return sign + Math.round(abs / 10000) + "만";
  return n.toLocaleString();
}

function fmtKRW(n: number): string {
  if (n < 0) return "-" + Math.abs(n).toLocaleString() + "원";
  return n.toLocaleString() + "원";
}

function calcScore(entry: Omit<CompareEntry, "score">, item: AuctionItem): number {
  let s = 0;
  if (entry.normalROI >= 20) s += 3;
  else if (entry.normalROI >= 10) s += 2;
  else if (entry.normalROI >= 0) s += 1;
  if (!entry.revDanger) s += 2;
  if (item.grade === "A") s += 2;
  else if (item.grade === "B") s += 1;
  if (!item.hasTenant) s += 1;
  if (item.failureCount === 0) s += 1;
  return Math.min(5, Math.round((s / 9) * 5));
}

const gradeColor: Record<string, string> = {
  A: "bg-emerald-500",
  B: "bg-amber-500",
  C: "bg-gray-400",
};

/** 주어진 숫자 배열에서 max/min 찾기 */
function findBestIdx(vals: number[], higherIsBetter: boolean): number {
  if (vals.length === 0) return -1;
  let bestIdx = 0;
  for (let i = 1; i < vals.length; i++) {
    if (higherIsBetter ? vals[i] > vals[bestIdx] : vals[i] < vals[bestIdx]) {
      bestIdx = i;
    }
  }
  return bestIdx;
}

interface Props {
  items: AuctionItem[];
  onRemove: (id: string) => void;
}

export default function CompareGrid({ items, onRemove }: Props) {
  const entries = useMemo<CompareEntry[]>(() => {
    return items.map((item) => {
      const normalData = auctionToNormalBidData(item);
      const reverseData = auctionToReverseBidData(item);
      const nr = calculateBid(normalData);
      const rr = calculateReverse(reverseData);
      const base: Omit<CompareEntry, "score"> = {
        item,
        normalRec: nr.recommendedBid,
        normalSafe: nr.safeBid,
        normalROI: nr.estimatedROI,
        revMax: rr.maxBidPrice,
        revSafe: rr.safeBid,
        revROI: rr.effectiveROI,
        revDanger: rr.dangerLineViolation,
        revWarning: rr.isOverMinBid,
        totalCost: nr.costs.totalCost,
        repairCost: nr.costs.repairCost,
        acquisitionTax: nr.costs.acquisitionTax,
      };
      return { ...base, score: calcScore(base, item) };
    });
  }, [items]);

  const n = entries.length;

  // Best value indexes per metric
  const bestROI = findBestIdx(entries.map(e => e.normalROI), true);
  const bestRevMax = findBestIdx(entries.filter(e => !e.revDanger).map(e => e.revMax), true);
  const bestRevROI = findBestIdx(entries.map(e => e.revROI), true);
  const bestCost = findBestIdx(entries.map(e => e.totalCost), false);
  const bestRepair = findBestIdx(entries.map(e => e.repairCost), false);
  const bestScore = findBestIdx(entries.map(e => e.score), true);

  const colStyle = `min-w-[180px] flex-1`;

  /** 셀 하이라이트 helper */
  function cellClass(isBest: boolean, isDanger = false): string {
    if (isDanger) return "text-rose-500 font-bold";
    if (isBest) return "text-emerald-600 font-bold";
    return "text-[#1C1917] font-semibold";
  }

  function BestBadge({ show }: { show: boolean }) {
    if (!show) return null;
    return <span className="ml-1 px-1.5 py-0.5 bg-emerald-100 text-emerald-700 text-[9px] font-bold rounded">최고</span>;
  }

  function ScoreStars({ score }: { score: number }) {
    return (
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map(s => (
          <i key={s} className={`text-sm ${s <= score ? "ri-star-fill text-[#C4956A]" : "ri-star-line text-[#D8D5D0]"}`}></i>
        ))}
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] as const }}
    >
      {/* 종합 점수 요약 배너 */}
      <div className="grid gap-3 mb-6" style={{ gridTemplateColumns: `repeat(${n}, 1fr)` }}>
        {entries.map((e, idx) => (
          <div
            key={e.item.id}
            className={`bg-white rounded-xl border-2 p-4 ${idx === bestScore ? "border-[#C4956A]" : "border-[#E8E5E0]"}`}
          >
            <div className="flex items-start justify-between mb-2">
              <span className={`px-2 py-0.5 text-[10px] font-bold text-white rounded-full ${gradeColor[e.item.grade] ?? "bg-gray-400"}`}>
                {e.item.grade}등급
              </span>
              {idx === bestScore && (
                <span className="px-2 py-0.5 bg-[#C4956A] text-white text-[10px] font-bold rounded-full whitespace-nowrap">
                  🏆 종합 1위
                </span>
              )}
            </div>
            <p className="text-xs font-bold text-[#1C1917] mb-0.5 line-clamp-2">{e.item.location}</p>
            <p className="text-[10px] text-[#8C8780] mb-3">{e.item.propertyType} · {e.item.court}</p>
            <ScoreStars score={e.score} />
            <p className="text-[10px] text-[#B8B3AE] mt-1">종합 점수 {e.score}/5</p>
          </div>
        ))}
      </div>

      {/* 비교 테이블 */}
      <div className="bg-white rounded-xl border border-[#E8E5E0] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" style={{ minWidth: `${180 + n * 200}px` }}>
            {/* 물건 헤더 */}
            <thead>
              <tr className="border-b-2 border-[#1C1917]">
                <th className="text-left px-5 py-4 text-xs font-bold text-[#8C8780] bg-[#FAFAF8] w-44">
                  비교 항목
                </th>
                {entries.map((e, idx) => (
                  <th key={e.item.id} className="px-4 py-4 text-left bg-[#FAFAF8]">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <div className="flex items-center gap-1.5 mb-1">
                          <span className={`px-2 py-0.5 text-[10px] font-bold text-white rounded-full ${gradeColor[e.item.grade] ?? "bg-gray-400"}`}>
                            {e.item.grade}
                          </span>
                          <span className="text-[10px] text-[#8C8780]">유찰 {e.item.failureCount}회</span>
                        </div>
                        <p className="text-xs font-bold text-[#1C1917] line-clamp-1">{e.item.location}</p>
                        <p className="text-[10px] text-[#8C8780]">{e.item.propertyType}</p>
                      </div>
                      <button
                        onClick={() => onRemove(e.item.id)}
                        className="w-5 h-5 rounded-full bg-[#F0EDE8] flex items-center justify-center hover:bg-rose-100 transition-colors cursor-pointer flex-shrink-0 mt-0.5"
                      >
                        <i className="ri-close-line text-[10px] text-[#8C8780] hover:text-rose-500"></i>
                      </button>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {/* ─── 기본 정보 ─── */}
              <SectionRow label="기본 정보" colCount={n} />
              <DataRow label="감정가">
                {entries.map(e => (
                  <td key={e.item.id} className="px-4 py-3 text-xs text-[#5C5650] border-b border-[#F8F5F2]">
                    {fmtShort(e.item.appraisedValue)}원
                  </td>
                ))}
              </DataRow>
              <DataRow label="최저 입찰가">
                {entries.map(e => (
                  <td key={e.item.id} className="px-4 py-3 text-xs font-bold text-[#C4956A] border-b border-[#F8F5F2]">
                    {fmtShort(e.item.minimumPrice)}원
                  </td>
                ))}
              </DataRow>
              <DataRow label="예상 매도가">
                {entries.map(e => (
                  <td key={e.item.id} className="px-4 py-3 text-xs text-[#5C5650] border-b border-[#F8F5F2]">
                    {fmtShort(e.item.expectedSalePrice)}원
                  </td>
                ))}
              </DataRow>
              <DataRow label="입찰일">
                {entries.map(e => (
                  <td key={e.item.id} className="px-4 py-3 text-xs text-[#5C5650] border-b border-[#F8F5F2]">
                    {e.item.auctionDate}
                  </td>
                ))}
              </DataRow>
              <DataRow label="임차인">
                {entries.map(e => (
                  <td key={e.item.id} className="px-4 py-3 border-b border-[#F8F5F2]">
                    {e.item.hasTenant
                      ? <span className="text-xs text-rose-500 font-medium">있음</span>
                      : <span className="text-xs text-emerald-600 font-medium">없음</span>
                    }
                  </td>
                ))}
              </DataRow>

              {/* ─── 일반방식 결과 ─── */}
              <SectionRow label="📐 일반방식 결과" colCount={n} accent />
              <DataRow label="추천 입찰가">
                {entries.map((e, idx) => (
                  <td key={e.item.id} className="px-4 py-3 border-b border-[#F8F5F2]">
                    <span className={`text-xs ${cellClass(idx === bestROI, e.normalRec < 0)}`}>
                      {e.normalRec < 0 ? "손실 위험" : fmtKRW(e.normalRec)}
                    </span>
                    <BestBadge show={idx === bestROI && e.normalRec > 0} />
                  </td>
                ))}
              </DataRow>
              <DataRow label="보수적 입찰가">
                {entries.map(e => (
                  <td key={e.item.id} className="px-4 py-3 text-xs text-[#5C5650] border-b border-[#F8F5F2]">
                    {e.normalSafe < 0 ? "-" : fmtKRW(e.normalSafe)}
                  </td>
                ))}
              </DataRow>
              <DataRow label="예상 ROI">
                {entries.map((e, idx) => (
                  <td key={e.item.id} className="px-4 py-3 border-b border-[#F8F5F2]">
                    <span className={`text-sm ${cellClass(idx === bestROI, e.normalROI < 0)}`}>
                      {e.normalROI >= 0 ? "+" : ""}{e.normalROI}%
                    </span>
                    <BestBadge show={idx === bestROI} />
                  </td>
                ))}
              </DataRow>

              {/* ─── 시세역산방식 결과 ─── */}
              <SectionRow label="⚡ 시세역산방식 결과" colCount={n} accent />
              <DataRow label="투찰 상한가 (P)">
                {entries.map((e, idx) => {
                  const nonDangerEntries = entries.filter(en => !en.revDanger);
                  const localBestIdx = entries.indexOf(nonDangerEntries[findBestIdx(nonDangerEntries.map(en => en.revMax), true)]);
                  return (
                    <td key={e.item.id} className="px-4 py-3 border-b border-[#F8F5F2]">
                      {e.revDanger ? (
                        <span className="text-xs text-rose-500 font-bold flex items-center gap-1">
                          <i className="ri-alarm-warning-line"></i>
                          투찰 불가
                        </span>
                      ) : (
                        <>
                          <span className={`text-xs ${cellClass(idx === localBestIdx)}`}>
                            {fmtKRW(e.revMax)}
                          </span>
                          <BestBadge show={idx === localBestIdx} />
                        </>
                      )}
                    </td>
                  );
                })}
              </DataRow>
              <DataRow label="역산 보수적 상한가">
                {entries.map(e => (
                  <td key={e.item.id} className="px-4 py-3 text-xs text-[#5C5650] border-b border-[#F8F5F2]">
                    {e.revDanger ? "-" : fmtKRW(e.revSafe)}
                  </td>
                ))}
              </DataRow>
              <DataRow label="역산 ROI">
                {entries.map((e, idx) => (
                  <td key={e.item.id} className="px-4 py-3 border-b border-[#F8F5F2]">
                    {e.revDanger ? (
                      <span className="text-xs text-rose-500">N/A</span>
                    ) : (
                      <>
                        <span className={`text-sm ${cellClass(idx === bestRevROI, e.revROI < 0)}`}>
                          {e.revROI >= 0 ? "+" : ""}{e.revROI}%
                        </span>
                        <BestBadge show={idx === bestRevROI && !e.revDanger} />
                      </>
                    )}
                  </td>
                ))}
              </DataRow>
              <DataRow label="위험도">
                {entries.map(e => (
                  <td key={e.item.id} className="px-4 py-3 border-b border-[#F8F5F2]">
                    {e.revDanger ? (
                      <span className="text-[10px] px-2 py-0.5 bg-rose-100 text-rose-600 rounded font-medium">손실 위험</span>
                    ) : e.revWarning ? (
                      <span className="text-[10px] px-2 py-0.5 bg-amber-100 text-amber-600 rounded font-medium">최저가 미달</span>
                    ) : (
                      <span className="text-[10px] px-2 py-0.5 bg-emerald-100 text-emerald-600 rounded font-medium">투찰 가능</span>
                    )}
                  </td>
                ))}
              </DataRow>

              {/* ─── 비용 비교 ─── */}
              <SectionRow label="💰 비용 비교" colCount={n} />
              <DataRow label="총 부대비용">
                {entries.map((e, idx) => (
                  <td key={e.item.id} className="px-4 py-3 border-b border-[#F8F5F2]">
                    <span className={`text-xs ${cellClass(idx === bestCost)}`}>
                      {fmtShort(e.totalCost)}원
                    </span>
                    {idx === bestCost && <BestBadge show />}
                  </td>
                ))}
              </DataRow>
              <DataRow label="수리비 추정">
                {entries.map((e, idx) => (
                  <td key={e.item.id} className="px-4 py-3 border-b border-[#F8F5F2]">
                    <span className={`text-xs ${cellClass(idx === bestRepair)}`}>
                      {fmtShort(e.repairCost)}원
                    </span>
                    {idx === bestRepair && <BestBadge show />}
                  </td>
                ))}
              </DataRow>
              <DataRow label="취득세 추정">
                {entries.map(e => (
                  <td key={e.item.id} className="px-4 py-3 text-xs text-[#5C5650] border-b border-[#F8F5F2]">
                    {fmtShort(e.acquisitionTax)}원
                  </td>
                ))}
              </DataRow>

              {/* ─── 종합 평가 ─── */}
              <SectionRow label="🏆 종합 평가" colCount={n} />
              <tr>
                <td className="px-5 py-2 text-xs text-[#8C8780] bg-[#FAFAF8]">투자 점수</td>
                {entries.map((e, idx) => (
                  <td key={e.item.id} className="px-4 py-3 border-b border-[#F8F5F2]">
                    <div className="flex flex-col gap-1">
                      <div className="flex gap-0.5">
                        {[1, 2, 3, 4, 5].map(s => (
                          <i key={s} className={`text-base ${s <= e.score ? "ri-star-fill text-[#C4956A]" : "ri-star-line text-[#D8D5D0]"}`}></i>
                        ))}
                      </div>
                      <span className="text-[10px] text-[#B8B3AE]">{e.score}/5점</span>
                      {idx === bestScore && (
                        <span className="text-[10px] px-2 py-0.5 bg-[#C4956A] text-white rounded-full font-bold w-fit">🏆 1위</span>
                      )}
                    </div>
                  </td>
                ))}
              </tr>
              <tr>
                <td className="px-5 py-4 text-xs text-[#8C8780] bg-[#FAFAF8]">상세 계산</td>
                {entries.map(e => (
                  <td key={e.item.id} className="px-4 py-4">
                    <div className="flex flex-col gap-1.5">
                      <Link
                        to={`/bid-calculator?itemId=${e.item.id}&mode=normal`}
                        className="flex items-center justify-center gap-1 px-3 py-1.5 text-[10px] font-semibold bg-[#1C1917] text-white rounded-full hover:bg-[#C4956A] transition-colors whitespace-nowrap"
                      >
                        <i className="ri-calculator-line text-xs"></i>
                        일반방식으로
                      </Link>
                      <Link
                        to={`/bid-calculator?itemId=${e.item.id}&mode=reverse`}
                        className="flex items-center justify-center gap-1 px-3 py-1.5 text-[10px] font-semibold border border-[#C4956A] text-[#C4956A] rounded-full hover:bg-[#C4956A]/5 transition-colors whitespace-nowrap"
                      >
                        <i className="ri-flashlight-line text-xs"></i>
                        시세역산으로
                      </Link>
                    </div>
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* 주석 */}
      <div className="mt-4 flex flex-wrap gap-4 text-[10px] text-[#B8B3AE]">
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded bg-emerald-100 inline-block"></span>
          <span className="text-emerald-600 font-bold">최고</span> = 해당 지표 최우수 값
        </span>
        <span>* 계산 기준: 1주택자, 대출 1.5억 / 4.5% / 6개월 기준 (개별 조정은 상세 계산기에서)</span>
      </div>
    </motion.div>
  );
}

/** 섹션 구분 행 */
function SectionRow({ label, colCount, accent }: { label: string; colCount: number; accent?: boolean }) {
  return (
    <tr>
      <td
        colSpan={colCount + 1}
        className={`px-5 py-2.5 text-xs font-bold border-y ${accent ? "bg-[#C4956A]/8 text-[#1C1917] border-[#C4956A]/20" : "bg-[#F5F2EE] text-[#5C5650] border-[#E8E5E0]"}`}
      >
        {label}
      </td>
    </tr>
  );
}

/** 데이터 행 — 라벨 컬럼 + children (td 들) */
function DataRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <tr>
      <td className="px-5 py-3 text-xs text-[#8C8780] bg-[#FAFAF8] border-b border-[#F8F5F2] align-middle whitespace-nowrap">
        {label}
      </td>
      {children}
    </tr>
  );
}